/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 24-07-2026
 *
 * Gedeelde opzoeking van ingrediënt-uitleg, gebruikt door de uitleg bij een node
 * (`dl-node-dom-uitleg`) en door de uitleg bij een nodemenu-item (`dl-node-menu-uitleg`).
 * De teksten staan in de vertalingen onder `INGREDIENT_UITLEG.{TYPE}[_{niveau}]`, waarbij een tekst
 * met een ` | ` gesplitst wordt in een korte en een volledige variant ("lees meer").
 */
import type {TranslateService} from '@ngx-translate/core';
import {elements} from 'dynalearn-common/lib/elements';

/**
 * De gevonden uitleg bij een type. `kort` is de tekst inclusief de "lees meer"-staart wanneer er een
 * volledige variant is; `vol` is leeg als de tekst niet uitklapbaar is. Beide leeg = niets gevonden.
 */
export interface IngredientUitleg {
    kort: string;
    vol: string;
}

/**
 * Zoekt de uitlegtekst voor een type op het opgegeven modelleerniveau. Loopt van specifiek naar
 * generiek: eerst het niveau zelf, dan per tiental omlaag tot 0 (zonder suffix), en binnen elk
 * niveau van het type zelf omhoog door de supertype-keten.
 */
export function zoekIngredientUitleg(translateService: TranslateService, type: string, niveau: number): IngredientUitleg {
    if (!type) {
        return {kort: '', vol: ''};
    }
    for (const niv of niveausVoorLookup(niveau)) {
        for (const t of [type, ...elements.alleUps(type)]) {
            const sleutel = niv ? `INGREDIENT_UITLEG.${t.toUpperCase()}_${niv}` : `INGREDIENT_UITLEG.${t.toUpperCase()}`;
            const html = translateService.instant(sleutel);
            if (html !== sleutel) {
                const delen = String(html).split(/\s*\|\s*/g);
                return delen.length > 1
                    ? {kort: `${delen[0]}<br><br>${translateService.instant('INGREDIENT_UITLEG.LEESMEER')}`, vol: delen.join('<br><br>')}
                    : {kort: delen[0], vol: ''};
            }
        }
    }
    return {kort: '', vol: ''};
}

/**
 * De niveaus waarop achtereenvolgens gezocht wordt: het niveau zelf, daarna afgerond per tiental
 * omlaag, eindigend bij 0 (de sleutel zonder niveau-suffix).
 */
function niveausVoorLookup(niveau: number): number[] {
    const niveaus = [niveau];
    let afgerond = Math.floor(niveau / 10) * 10;
    if (afgerond !== niveau) {
        niveaus.push(afgerond);
    }
    while (afgerond > 0) {
        afgerond -= 10;
        niveaus.push(afgerond);
    }
    return niveaus;
}
